import getConfig from 'temp/config';

export enum PageName {
  BlueParrottForms = 'blueparrott-forms',
  SupportForms = 'support-forms',
  StoreForms = 'forms',
  TieredServicePortalForms = 'tsp-forms',
  GnSupportForms = 'gn-support-forms',
  StoreFormsAnyRoute = '',
}

export enum SiteName {
  Support = 'njord-support',
  Store = 'njord-store',
  TieredServicePortal = 'tiered-service-portal',
  BlueParrott = 'njord-blueparrott',
}

export type ApiKeys = {
  storeApiKey: string;
  supportApiKey: string;
  tieredServicePortalApiKey: string;
};

export type SitecoreConfig = ReturnType<typeof getConfig>;

export const pageSiteName = {
  [PageName.SupportForms]: SiteName.Support,
  [PageName.StoreForms]: SiteName.Store,
  [PageName.TieredServicePortalForms]: SiteName.TieredServicePortal,
  [PageName.StoreFormsAnyRoute]: SiteName.Store,
  [PageName.BlueParrottForms]: SiteName.BlueParrott,
  [PageName.GnSupportForms]: SiteName.Support,
} as const;

export const getPageNameFromUrl = (url: string): PageName => {
  const pageName = url
    .split('/')
    .filter((path: PageName) => !!path && pageSiteName[path])
    .at(0);
  return (pageName || PageName.StoreForms) as PageName;
};

export const getSiteNameFromUrl = (url: string): SiteName => {
  return pageSiteName[getPageNameFromUrl(url)];
};

/**
 * Gets the appropriate Sitecore API host based on the site name
 * @param {SiteName} siteName - The site name
 * @param {SitecoreConfig} config - The Sitecore configuration
 * @returns {string} The API host URL
 */
export const getApiHost = (siteName: SiteName, config: SitecoreConfig): string => {
  switch (siteName) {
    case SiteName.Support:
      return config.sitecoreApiHostSupport;
    case SiteName.TieredServicePortal:
      return config.sitecoreApiHostTieredServicePortal;
    default:
      return config.sitecoreApiHost;
  }
};

/**
 * Gets the appropriate Sitecore API key based on the site name
 * @param {SiteName} siteName - The site name
 * @param {SitecoreConfig} config - The Sitecore configuration
 * @returns {string} The API key
 */
export const getApiKey = (siteName: SiteName, config: SitecoreConfig): string => {
  switch (siteName) {
    case SiteName.Support:
      return config.sitecoreApiKeySupport;
    case SiteName.TieredServicePortal:
      return config.sitecoreApiKeyTieredServicePortal;
    default:
      return config.sitecoreApiKey;
  }
};

/**
 * Gets the API prefix path based on the page name
 * @param {PageName} pageName - The page name
 * @returns {string} The route prefix path for the page (empty string for store forms)
 */
export const getApiPrefix = (pageName: PageName): string => {
  switch (pageName) {
    case PageName.SupportForms:
      return `/${PageName.SupportForms}`;
    case PageName.GnSupportForms:
      return `/${PageName.GnSupportForms}`;
    case PageName.TieredServicePortalForms:
      return `/${PageName.TieredServicePortalForms}`;
    case PageName.BlueParrottForms:
      return `/${PageName.BlueParrottForms}`;
    default:
      return PageName.StoreFormsAnyRoute;
  }
};

/**
 * Gets the appropriate API key based on the site name (for client-side usage)
 * @param {SiteName} siteName - The site name
 * @param {ApiKeys} apiKeys - The API keys object
 * @returns {string} The API key
 */
export const getApiKeyForSite = (siteName: SiteName, apiKeys: ApiKeys): string => {
  switch (siteName) {
    case SiteName.Support:
      return apiKeys.supportApiKey;
    case SiteName.TieredServicePortal:
      return apiKeys.tieredServicePortalApiKey;
    default:
      return apiKeys.storeApiKey;
  }
};
